import { Button, Table, type TableColumnsType } from 'antd';
import { Plus, type LucideIcon } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import type { CatalogueState } from '../hooks/use-catalogue';
import { useCatalogueDrawer } from '../hooks/use-catalogue-drawer';

export interface CatalogueDrawerProps<Row, Input> {
  open: boolean;
  /** Present to edit that row; absent to create a new one. */
  editing?: Row;
  error: string | null;
  submitting: boolean;
  onSubmit: (input: Input) => Promise<boolean>;
  onClose: () => void;
}

export interface CatalogueRowHandlers<Row> {
  onEdit: (row: Row) => void;
  onDeactivate: (row: Row) => Promise<void>;
  onReactivate: (row: Row) => Promise<void>;
}

interface CataloguePanelProps<Row extends { id: string }, Input> {
  /** "container size": names the New button, the skeleton and the empty state. */
  noun: string;
  plural: string;
  icon: LucideIcon;
  emptyDescription: string;
  /** Shown above the table, e.g. that the rows are placeholders. */
  notice?: ReactNode;
  state: CatalogueState<Row>;
  saveError: string | null;
  saving: boolean;
  resetSaveError: () => void;
  create: (input: Input) => Promise<boolean>;
  update: (id: string, input: Input) => Promise<boolean>;
  deactivate: (row: Row) => Promise<void>;
  reactivate: (row: Row) => Promise<void>;
  buildColumns: (actions: CatalogueRowHandlers<Row>) => TableColumnsType<Row>;
  Drawer: ComponentType<CatalogueDrawerProps<Row, Input>>;
}

/** The table, New button, empty state and create/edit drawer of one admin-managed catalogue. */
export function CataloguePanel<Row extends { id: string }, Input>({
  noun,
  plural,
  icon,
  emptyDescription,
  notice,
  state,
  saveError,
  saving,
  resetSaveError,
  create,
  update,
  deactivate,
  reactivate,
  buildColumns,
  Drawer,
}: CataloguePanelProps<Row, Input>) {
  const drawer = useCatalogueDrawer<Row>(resetSaveError);

  const newButton = (
    <Button size="small" icon={<Plus className="size-4" />} onClick={() => drawer.open()}>
      New {noun}
    </Button>
  );
  const rows = state.status === 'loaded' ? state.rows : [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-fg">{plural.charAt(0).toUpperCase() + plural.slice(1)}</h3>
        {newButton}
      </div>
      {notice}
      {state.status === 'loading' && <TableSkeleton label={`Loading ${plural}`} />}
      {state.status === 'error' && <EmptyState icon={icon} title={`Couldn't load ${plural}`} description="Try reloading the page." />}
      {state.status === 'loaded' && rows.length === 0 && (
        <EmptyState icon={icon} title={`No ${plural} yet`} description={emptyDescription}>
          {newButton}
        </EmptyState>
      )}
      {rows.length > 0 && (
        <Table
          rowKey="id"
          size="small"
          dataSource={rows}
          columns={buildColumns({ onEdit: drawer.open, onDeactivate: deactivate, onReactivate: reactivate })}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
        />
      )}
      <Drawer
        open={drawer.isOpen}
        editing={drawer.editing}
        error={saveError}
        submitting={saving}
        onSubmit={(input) => (drawer.editing ? update(drawer.editing.id, input) : create(input))}
        onClose={drawer.close}
      />
    </div>
  );
}
